import { ref, inject } from "vue";
import { useAuthStore } from "@/store/auth.store";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import SavingRepository from "@/repositories/SavingRepository";
import { Account } from "@/models/Account";

export default class TransferDialogViewModel {
  public dialog = ref(false);
  public accounts = ref<Account[]>([]); // Asegura que siempre sea un arreglo
  public selectedAccount = ref<Account | null>(null); // Se espera un objeto de cuenta completo
  public amount = ref<number | null>(null);
  public loading = ref(false);

  private authStore = useAuthStore();
  private toast: Toast;

  constructor() {
    this.toast = inject("toast") as Toast;
  }

  /**
   * Abre el diálogo y carga las cuentas
   * @param accounts Lista de cuentas disponibles
   */
  public openDialog(accounts: Account[]): void {
    if (!Array.isArray(accounts)) {
      console.error("Error: Las cuentas no son un array válido.");
      this.accounts.value = [];
      return;
    }

    // Validar y asignar cuentas
    this.accounts.value = accounts.filter(
      (account) => account.nom && account.idbancocuenta
    );

    if (this.accounts.value.length === 0) {
      this.toast.showToast(
        "No hay cuentas válidas para mostrar.",
        ToastType.Info
      );
      return;
    }

    this.dialog.value = true;
  }

  /**
   * Cierra el diálogo y resetea los valores
   */
  public closeDialog = (): void => {
    this.resetDialog();
  };

  /**
   * Resetea los valores del diálogo
   */
  public resetDialog = (): void => {
    this.dialog.value = false;
    this.selectedAccount.value = null;
    this.amount.value = null;
    this.loading.value = false;
    this.accounts.value = []; // Limpiar cuentas
  };

  /**
   * Confirma la solicitud de transferencia
   */
  public confirm = async (): Promise<void> => {
    console.log(this.selectedAccount.value);
    if (!this.selectedAccount.value || !this.amount.value) {
      this.toast.showToast(
        "Seleccione una cuenta y un monto válido.",
        ToastType.Info
      );
      return;
    }

    if (this.amount.value > 10000) {
      this.toast.showToast(
        "El monto no puede superar los 10,000.",
        ToastType.Error
      );
      return;
    }

    this.loading.value = true;

    try {
      const login = await this.authStore.getLogin;
      const password = await this.authStore.getPassword;
      const authBasic = `${btoa(`${login}:${password}`)}`;

      const formData = new FormData();
      formData.append(
        "idbancocuenta",
        this.selectedAccount.value?.idbancocuenta.toString()
      );
      formData.append("monto", this.amount.value.toString());
      formData.append(
        "moneda",
        this.selectedAccount.value?.moneda.toString() ?? ""
      );

      await SavingRepository.setTransferenciaAplic(authBasic, formData);

      this.toast.showToast(
        "Su transferencia se esta procesando, cuando este lista se mostrara en sus movimientos.",
        ToastType.Success
      );
      this.loading.value = false;
      this.resetDialog();
    } catch (error) {
      console.error("Error al realizar la transferencia:", error);
      this.toast.showToast(
        "No se pudo realizar la solicitud transferencia. Intente nuevamente.",
        ToastType.Error
      );
      this.loading.value = false;
    }
  };

  /**
   * Devuelve el símbolo de la moneda
   * @param moneda Código de la moneda
   */
  public getCoin(moneda: number): string {
    return moneda === 1 ? "S/." : "$";
  }
}
